import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { DefaultRootState } from "@reduxLocal/persistState";
import { CONTEXT_SIZE_TOGGLE, TOGGLE_CONTEXT } from "@reduxLocal/actions/types";
import * as Animatable from 'react-native-animatable';
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import styles from "../../../stylesheet";
import { Platform, Pressable } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import webTransition from '@utils/webTransition';
import { useWindowDimensions } from 'react-native';
// The PageContext component is no longer needed as its logic is now inside ContextHandler.

const ContextHandler = ({ route, navigation, contextProps = {} }) => {
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { width, height } = useWindowDimensions();

  // Get state from Redux
  const currentScreen = useSelector((state: DefaultRootState) => state.navigation.page);
  const minimized = useSelector((state: DefaultRootState) => state.UI.contextMinimized);
  const { toolbarDisplayed } = useSelector((state: DefaultRootState) => state.modals.toolbar);
  const contextAvailable = useSelector((state: DefaultRootState) => state.UI.contextAvailable);


  // State to hold the dynamically imported component and its status
  const [hasContext, setHasContext] = useState(false);
  // NOTE: Use a capital letter for state that holds a component reference
  const [DynamicContext, setDynamicContext] = useState(null);

  // Create a clone of DynamicContext that can accept props
  const createContextClone = useCallback((customProps = {}) => {
    if (!DynamicContext) return null;

    // Create a wrapper component that can accept additional props
    const ContextWithProps = React.forwardRef((props, ref) => {
      const mergedProps = {
        navigation,
        route,
        minimized,
        theme,
        width,
        height,
        ...customProps,
        ...props,
      };

      return React.createElement(DynamicContext, mergedProps);
    });

    return ContextWithProps;
  }, [DynamicContext, navigation, route, minimized, theme, width, height]);

  // Utility function to create a context clone with specific props
  const createContextWithProps = useCallback((specificProps = {}) => {
    return createContextClone(specificProps);
  }, [createContextClone]);

  // ✅ NEW: This useEffect now correctly handles loading the context.
  useEffect(() => {
    let isMounted = true; // Prevent state updates if component unmounts during async operation

    const loadContext = async () => {
      const importFrom = () => {
        const routesAvailable = currentScreen.state?.routes;
        return routesAvailable ? routesAvailable[routesAvailable.length - 1].name : currentScreen.name;
      };

      try {
        const contextModule = await import(`../screens/MidLevel/${currentScreen.name}Screen/Context/${importFrom()}Context`);
        if (isMounted) {
          // If import succeeds, store the component and set hasContext to true
          setDynamicContext(() => contextModule.default);
          setHasContext(true);
          if (window.innerWidth > 1200) {
            dispatch({ type: TOGGLE_CONTEXT, payload: true });
          }
        }
      } catch (e) {
        if (isMounted) {
          // If import fails, reset the state
          setDynamicContext(null);
          setHasContext(false);
          dispatch({ type: TOGGLE_CONTEXT, payload: false });
        }
      }
    };

    loadContext();

    // Cleanup function to reset state and prevent memory leaks
    return () => {
      isMounted = false;
      setHasContext(false);
      setDynamicContext(null);
    };
  }, [currentScreen, dispatch]);


  // The conditional return now works, because the useEffect above will trigger a 
  // re-render with the correct `hasContext` value.
  if (!hasContext || !DynamicContext || width < 900) {
    return null;
  }

  // This style is only calculated if the component is going to render.
  const containerStyle = {
    top: 0,
    height: '100%' as const,
    paddingRight: 20,
    zIndex: 900,
    width: minimized ? 140 : 360,
    backgroundColor: theme ? "rgb(213,213,213)" : "#333333",
    overflow: "visible" as const,
    ...webTransition('width'),
  };

  // Responsive breakpoints
  const isSmall = width < 768;
  const isMedium = width >= 768 && width < 1024;
  const isLarge = width >= 1024 && width < 1440;
  const isXLarge = width >= 1440;

  return hasContext && (
    <Animatable.View style={containerStyle}>
      <Animatable.View
        useNativeDriver={false}
        transition={['padding']}
        style={[styles.contextContainer, {
          flex: 1,
          padding: minimized ? 5 : 10,
          backgroundColor: "rgba(0,0,0,0.3)", 
          marginVertical: 15,
          borderRadius: 24,
          left: 10
        }]}
      >
        {contextAvailable && (
          <Pressable
            onPress={() => dispatch({ type: CONTEXT_SIZE_TOGGLE })}
            style={{
              zIndex: 1,
              width: isSmall ? 35 : 40,
              height: isSmall ? 35 : 40,
              backgroundColor: "rgb(90,192,234)",
              borderRadius: 30,
              justifyContent: 'center',
              alignItems: 'center',
              marginLeft: 8
            }}
          >
            <Animatable.View
              className='animation-comp'
              transition="rotate"
              style={{ transform: [{ rotate: minimized ? '180deg' : '0deg' }] }}
            >
              <FontAwesome
                name={"arrow-circle-right"}
                size={isSmall ? 20 : 25}
                color="white"
              />
            </Animatable.View>
          </Pressable>
        )}

        {/* Render the dynamically imported component using the clone */}
        {Platform.OS === 'web' && (() => {
          const ClonedContext = createContextClone({
            // Additional props that can be passed to the context
            isSmall,
            isMedium,
            isLarge,
            isXLarge,
            toolbarDisplayed,
            contextAvailable,
            // Context-specific props for the new ContextWrapper pattern
            actionArray: contextProps.actionArray || [],
            defaultComponent: contextProps.defaultComponent,
            contextComponents: contextProps.contextComponents || {},
            contextProps: contextProps.contextProps || {},
            customStyling: contextProps.customStyling || {},
            // Merge any props passed from parent component
            ...contextProps,
            // Add any other props you want to pass
            customData: {
              screenName: currentScreen.name,
              timestamp: Date.now(),
              ...(contextProps as any).customData,
            }
          });

          return ClonedContext ? <ClonedContext /> : null;
        })()}

      </Animatable.View>
    </Animatable.View>
  );
};

export default React.memo(ContextHandler);